import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useLanguage } from "../context/LanguageContext";
import { Reveal, MaskedLine } from "./motion";
import { trackEvent } from "../analytics/ga4";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full border-b border-white/20 bg-transparent py-4 text-base font-light text-white placeholder:text-white/35 focus:border-wave focus:outline-none transition-colors duration-500";

export const ContactSection = () => {
  const { t } = useLanguage();
  const c = t.contact;
  const [form, setForm] = useState({ name: "", email: "", organization: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          organization: form.organization || null,
          message: form.message,
        }),
      });
      if (!res.ok) throw new Error("failed");
      trackEvent("generate_lead");
      toast.success(c.form.success);
      setForm({ name: "", email: "", organization: "", message: "" });
    } catch {
      toast.error(c.form.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="kontakt" data-testid="contact-section" className="grain relative overflow-hidden bg-deep text-white">
      <div className="pointer-events-none absolute -right-40 -top-40 h-[480px] w-[480px] rounded-full bg-deep/50 blur-3xl" />
      <div className="relative mx-auto max-w-[1600px] px-6 py-28 sm:px-12 sm:py-40 md:px-16">
        <div className="grid gap-20 lg:grid-cols-2">
          <div>
            <Reveal y={16}>
              <p className="text-[13px] font-medium uppercase tracking-label text-wave">{c.label}</p>
            </Reveal>
            <h2 className="font-head mt-8 text-4xl leading-[1.08] sm:text-6xl">
              <MaskedLine delay={0.1}>{c.title}</MaskedLine>
            </h2>
            <Reveal delay={0.3} className="mt-8 max-w-md">
              <p className="text-base font-light leading-relaxed text-white/65 sm:text-lg">{c.text}</p>
            </Reveal>
            <Reveal delay={0.45} className="mt-14 space-y-8">
              <div>
                <p className="text-[10px] font-medium uppercase tracking-label text-white/40">{c.emailLabel}</p>
                <a
                  data-testid="contact-email-link"
                  href="mailto:david@davidfrank.at"
                  className="font-head mt-2 inline-block text-2xl text-white transition-colors duration-300 hover:text-wave sm:text-3xl"
                >
                  david@davidfrank.at
                </a>
              </div>
              <div>
                <p className="text-[10px] font-medium uppercase tracking-label text-white/40">{c.phoneLabel}</p>
                <a
                  data-testid="contact-phone-link"
                  href="tel:+4368181964809"
                  className="font-head mt-2 inline-block text-2xl text-white transition-colors duration-300 hover:text-wave sm:text-3xl"
                >
                  +43 681 81964809
                </a>
              </div>
              <div>
                <p className="text-[10px] font-medium uppercase tracking-label text-white/40">{c.linkedinLabel}</p>
                <a
                  data-testid="contact-linkedin-link"
                  href="https://www.linkedin.com/in/david-frank-780b501b1/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group mt-2 inline-flex items-center gap-4 text-[12px] font-medium uppercase tracking-[0.22em] text-white transition-colors duration-300 hover:text-wave"
                >
                  {c.linkedinCta}
                  <span className="h-px w-8 bg-current transition-all duration-500 group-hover:w-12" aria-hidden="true" />
                </a>
              </div>
              <p className="text-sm font-light text-white/45">{c.location}</p>
            </Reveal>
          </div>

          <Reveal delay={0.25}>
            <form data-testid="contact-form" onSubmit={submit} className="space-y-8 lg:pt-24" noValidate={false}>
              <div>
                <label htmlFor="cf-name" className="sr-only">{c.form.name}</label>
                <input
                  id="cf-name"
                  data-testid="contact-form-name-input"
                  required
                  minLength={2}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder={c.form.name}
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="cf-email" className="sr-only">{c.form.email}</label>
                <input
                  id="cf-email"
                  data-testid="contact-form-email-input"
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder={c.form.email}
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="cf-org" className="sr-only">{c.form.organization}</label>
                <input
                  id="cf-org"
                  data-testid="contact-form-organization-input"
                  value={form.organization}
                  onChange={(e) => setForm({ ...form, organization: e.target.value })}
                  placeholder={c.form.organization}
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="cf-message" className="sr-only">{c.form.message}</label>
                <textarea
                  id="cf-message"
                  data-testid="contact-form-message-input"
                  required
                  minLength={10}
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder={c.form.message}
                  className={`${inputCls} resize-none`}
                />
              </div>
              <button
                type="submit"
                data-testid="contact-form-submit-button"
                disabled={sending}
                className="group inline-flex items-center gap-4 border border-white/30 px-8 py-4 text-[11px] font-medium uppercase tracking-[0.22em] text-white transition-all duration-500 hover:bg-white hover:text-ink disabled:opacity-50"
              >
                {sending ? c.form.sending : c.form.submit}
                <span className="h-px w-6 bg-current transition-all duration-500 group-hover:w-10" />
              </button>
              <p data-testid="contact-form-privacy-note" className="!mt-4 max-w-md text-[12px] font-light leading-relaxed text-white/45">
                {c.form.privacyPrefix}{" "}
                <Link
                  to="/datenschutz"
                  data-testid="contact-form-privacy-link"
                  className="underline decoration-white/30 underline-offset-4 transition-colors hover:text-white hover:decoration-white/70"
                >
                  {c.form.privacyLink}
                </Link>
                .
              </p>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
